import React, { useState, useCallback } from "react";
import debounce from "lodash/debounce";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
  Spinner,
} from "reactstrap";
import { Select } from "antd";
import toast from "react-hot-toast";
import { MergeCustomersApi, getAllCustomers } from "../../api/pagination";

const MergeCustomerModal = ({ isOpen, toggle, gettingCustomers, customerType = "company" }) => {
  const [fromCustomerId, setFromCustomerId] = useState(null);
  const [toCustomerId, setToCustomerId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [searchCustomers, setSearchCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [confirmed, setConfirmed] = useState(false);

  const getCustomerLabel = (c) => {
    const name = `${c?.billing?.[0]?.first_name || ""} ${c?.billing?.[0]?.last_name || ""}`.trim();
    const company = c?.billing?.[0]?.company;
    return `${c?.id} - ${company || name}`;
  };

  const fetchCustomersDebounced = useCallback(
    debounce((val) => {
      getAllCustomers(1, val, 20, customerType, (res) => {
        setSearching(false);
        if (res.status === 200) {
          setSearchCustomers(res.data?.data || []);
        }
      });
    }, 500),
    [customerType]
  );

  const handleCustomerSearch = (val) => {
    if (!val || val.trim().length === 0) {
      setSearchCustomers([]);
      return;
    }
    setSearching(true);
    fetchCustomersDebounced(val.trim());
  };

  const handleMerge = () => {
    if (!confirmed) {
      toast.error("Please confirm you have read the message before merging");
      return;
    }
    if (!fromCustomerId || !toCustomerId) {
      toast.error("Please select both customers to merge");
      return;
    }
    if (fromCustomerId === toCustomerId) {
      toast.error("Cannot merge a customer into itself");
      return;
    }

    setMergeLoading(true);
    MergeCustomersApi(fromCustomerId, toCustomerId, (res) => {
      setMergeLoading(false);
      if (res.status === 200) {
        toast.success("Customers merged successfully!");
        gettingCustomers();
        toggle();
        setFromCustomerId(null);
        setToCustomerId(null);
        setSearchCustomers([]);
        setConfirmed(false);
      } else {
        toast.error(res.message || "Failed to merge customers");
      }
    });
  };

  React.useEffect(() => {
    if (isOpen) {
      setConfirmed(false);
      getAllCustomers(1, "", 20, customerType, (res) => {
        if (res.status === 200) {
          setAllCustomers(res.data?.data || []);
        }
      });
    } else {
      // Reset on close
      setFromCustomerId(null);
      setToCustomerId(null);
      setSearchCustomers([]);
      setConfirmed(false);
    }
  }, [isOpen, customerType]);

  const customerOptions = (searchCustomers.length > 0 ? searchCustomers : allCustomers)?.map(
    (c) => ({ value: c._id, label: getCustomerLabel(c) })
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="border-0 pb-0">
        <h5 className="fw-bold mb-0" style={{ color: "var(--primary-red)" }}>
          Merge Customers
        </h5>
      </ModalHeader>

      <ModalBody className="py-4">
        <div
          className="p-3 rounded-12 border border-dashed mb-3"
          style={{
            backgroundColor: "rgba(227, 27, 35, 0.05)",
            borderColor: "var(--primary-red)",
          }}
        >
          <p className="text-muted fs-13 mb-0">
            Consolidate another customer's data into another account.{" "}
            <strong>This action cannot be undone.</strong> The source customer
            will be permanently deleted after the merge.
          </p>
          <div className="mt-3 d-flex align-items-start gap-2 pt-2 border-top border-danger border-opacity-25">
            <input 
              type="checkbox" 
              id="confirm-merge-customer"
              className="mt-1 custom-checkbox"
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <label htmlFor="confirm-merge-customer" className="fs-12 text-dark fw-600 cursor-pointer mb-0">
              I understand that merging is permanent. All orders and data from the source will be moved to the target, and the source account will be deleted.
            </label>
          </div>
        </div>

        <Row className="g-3">
          <Col md={12}>
            <Label className="mb-1 fw-600 text-muted fs-12 uppercase">
              Source Customer (To be removed)
            </Label>
            <Select
              showSearch
              placeholder="Search source customer..."
              className="w-100 modern-antd-select"
              filterOption={false}
              onSearch={handleCustomerSearch}
              loading={searching}
              notFoundContent={
                searching ? (
                  <Spinner size="sm" color="primary" />
                ) : (
                  "No customers found"
                )
              }
              onChange={(val) => setFromCustomerId(val)}
              value={fromCustomerId}
              options={customerOptions}
            />
          </Col>

          <Col md={12}>
            <Label className="mb-1 fw-600 text-muted fs-12 uppercase">
              Destination Customer (Target)
            </Label>
            <Select
              showSearch
              placeholder="Search destination customer..."
              className="w-100 modern-antd-select"
              filterOption={false}
              onSearch={handleCustomerSearch}
              loading={searching}
              notFoundContent={
                searching ? (
                  <Spinner size="sm" color="primary" />
                ) : (
                  "No customers found"
                )
              }
              onChange={(val) => setToCustomerId(val)}
              value={toCustomerId}
              options={customerOptions}
            />
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="border-0 pt-0">
        <Button color="secondary" outline onClick={toggle} className="rounded-8 px-4">
          Cancel
        </Button>
        {confirmed && (
          <button
            className="red-button shadow-sm px-4"
            onClick={handleMerge}
            disabled={!fromCustomerId || !toCustomerId || mergeLoading}
            style={{ width: "auto" }}
          >
            {mergeLoading ? <Spinner size="sm" /> : "Merge Customers"}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default MergeCustomerModal;
